// src/app/time-attendance/components/payroll-sync/pages/payroll-booked-hours-history/payroll-booked-hours-history.component.ts

import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, firstValueFrom } from 'rxjs';

import { PayrollSyncService } from '../services/payroll-sync.service';
import { BookedHourHistoryDto, MiniUserDto } from '../models/payroll-sync.model';
import { PayrollNavComponent } from '../../../shared/payroll-nav/payroll-nav.component';

type ToastKind = 'success' | 'error' | 'info';

@Component({
  selector: 'app-payroll-booked-hours-history',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePipe, DecimalPipe, PayrollNavComponent],
  templateUrl: './payroll-booked-hours-history.component.html',
  styleUrls: ['./payroll-booked-hours-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PayrollBookedHoursHistoryComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  isLoading = true;
  errorMessage: string | null = null;

  toastMessage: string | null = null;
  toastKind: ToastKind = 'info';
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  rows: BookedHourHistoryDto[] = [];

  pageNumber = 1;
  pageSize = 10;
  totalCount = 0;
  totalPages = 1;

  readonly pageSizeOptions = [10, 25, 50, 100];

  constructor(
    private readonly payrollSyncSvc: PayrollSyncService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    void this.loadHistory();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearToast();
  }

  async loadHistory(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = null;
    this.detect();

    try {
      const response = await firstValueFrom(
        this.payrollSyncSvc.getBookedHoursHistory({
          pageNumber: this.pageNumber,
          pageSize: this.pageSize,
        }),
      );

      this.rows = response.data ?? [];
      this.totalCount = Number(response.totalCount ?? 0);
      this.totalPages = Math.max(1, Number(response.totalPages ?? 1));
      this.pageNumber = Number(response.currentPage ?? this.pageNumber);
      this.pageSize = Number(response.pageSize ?? this.pageSize);
    } catch (error) {
      console.error('getBookedHoursHistory error', error);
      this.errorMessage = 'Failed to load booked hours history.';
      this.rows = [];
      this.totalCount = 0;
      this.totalPages = 1;
      this.showToast('error', 'Failed to load booked hours history.');
    } finally {
      this.isLoading = false;
      this.detect();
    }
  }

  refresh(): void {
    void this.loadHistory();
  }

  previousPage(): void {
    if (this.pageNumber <= 1 || this.isLoading) {
      return;
    }

    this.pageNumber -= 1;
    void this.loadHistory();
  }

  nextPage(): void {
    if (this.pageNumber >= this.totalPages || this.isLoading) {
      return;
    }

    this.pageNumber += 1;
    void this.loadHistory();
  }

  changePageSize(raw: string): void {
    const nextPageSize = Number(raw);

    if (!Number.isFinite(nextPageSize) || nextPageSize <= 0) {
      return;
    }

    this.pageSize = nextPageSize;
    this.pageNumber = 1;
    void this.loadHistory();
  }

  getEmployeeName(row: BookedHourHistoryDto): string {
    return `${row.employee?.firstName ?? ''} ${row.employee?.lastName ?? ''}`.trim() || '—';
  }

  getEmployeeNumber(row: BookedHourHistoryDto): string {
    return row.employee?.employeeNumber || '—';
  }

  getBookedDate(row: BookedHourHistoryDto): string | null {
    return row.bookedDate ?? null;
  }

  getPayCodeLabel(row: BookedHourHistoryDto): string {
    const name = row.payCode?.name ?? '—';
    const type = row.payCode?.type ? ` (${row.payCode.type})` : '';

    return `${name}${type}`;
  }

  getPayGroupName(row: BookedHourHistoryDto): string {
    return row.payGroup?.name ?? '—';
  }

  getProjectName(row: BookedHourHistoryDto): string {
    return row.project?.name ?? '—';
  }

  getCompanyName(row: BookedHourHistoryDto): string {
    return row.company?.name ?? row.employee?.company?.name ?? '—';
  }

  getDepartmentLabel(row: BookedHourHistoryDto): string {
    const department = row.department ?? row.employee?.department ?? null;

    if (!department) {
      return '—';
    }

    const code = department.code !== null && department.code !== undefined ? String(department.code) : '';
    const name = department.name ?? '';

    if (code && name) {
      return `${code} - ${name}`;
    }

    return code || name || '—';
  }

  getWorkerTypeName(row: BookedHourHistoryDto): string {
    return row.workerType?.name ?? '—';
  }

  getWorkOrderLabel(row: BookedHourHistoryDto): string {
    if (!row.workOrderNumber) {
      return '—';
    }

    const won = row.workOrderNumber.won ?? '';
    const description = row.workOrderNumber.description ?? '';

    if (won && description) {
      return `${won} - ${description}`;
    }

    return won || description || '—';
  }

  getYywwLabel(row: BookedHourHistoryDto): string {
    return row.yyww?.name ?? row.yyww?.fullYearWeek ?? (row.yyww?.id ? String(row.yyww.id) : '—');
  }

  getApprovalStatus(row: BookedHourHistoryDto): 'Approved' | 'Employee Approved' | 'Pending' {
    if (row.supervisorApprovedDate || row.approvedBySupervisor || row.supervisorApprovedBy) {
      return 'Approved';
    }

    if (row.employeeApprovedDate || row.approvedByEmployee || row.employeeApprovedBy) {
      return 'Employee Approved';
    }

    return 'Pending';
  }

  getApprovalClass(row: BookedHourHistoryDto): 'approved' | 'employee-approved' | 'pending' {
    const status = this.getApprovalStatus(row);

    if (status === 'Approved') {
      return 'approved';
    }

    if (status === 'Employee Approved') {
      return 'employee-approved';
    }

    return 'pending';
  }

  getSupervisorApprovedBy(row: BookedHourHistoryDto): string {
    return this.getUserName(row.approvedBySupervisor ?? row.supervisorApprovedBy ?? null);
  }

  getSupervisorApprovedAt(row: BookedHourHistoryDto): string | null {
    return row.supervisorApprovedDate ?? null;
  }

  getEmployeeApprovedBy(row: BookedHourHistoryDto): string {
    return this.getUserName(row.approvedByEmployee ?? row.employeeApprovedBy ?? null);
  }

  getEmployeeApprovedAt(row: BookedHourHistoryDto): string | null {
    return row.employeeApprovedDate ?? null;
  }

  getCreatedBy(row: BookedHourHistoryDto): string {
    return this.getUserName(row.createdBy ?? null);
  }

  getUpdatedBy(row: BookedHourHistoryDto): string {
    return this.getUserName(row.updatedBy ?? null);
  }

  getCreatedAt(row: BookedHourHistoryDto): string | null {
    return row.createdAt ?? null;
  }

  getUpdatedAt(row: BookedHourHistoryDto): string | null {
    return row.updatedAt ?? null;
  }

  private getUserName(user: MiniUserDto | null | undefined): string {
    if (!user) {
      return '—';
    }

    return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.employeeNumber || '—';
  }

  clearToast(): void {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }

    this.toastTimer = null;
    this.toastMessage = null;
    this.detect();
  }

  private showToast(kind: ToastKind, message: string): void {
    this.toastKind = kind;
    this.toastMessage = message;

    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }

    this.toastTimer = setTimeout(() => this.clearToast(), 2600);
    this.detect();
  }

  private detect(): void {
    if (!(this.cdr as unknown as { destroyed?: boolean }).destroyed) {
      this.cdr.detectChanges();
    }
  }

  trackById(_: number, item: { id: number }): number {
    return item.id;
  }
}